import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClipboardCheck,
  faClipboardList,
  faMinus,
} from "@fortawesome/free-solid-svg-icons";

/**
 * Botão de status de parâmetros de produção
 *
 * Estados:
 * - Vermelho: Parâmetros pendentes (has_active_parameters=true, parameters_filled=false)
 * - Verde: Parâmetros preenchidos (has_active_parameters=true, parameters_filled=true)
 * - Cinza: Produto sem parâmetros configurados (has_active_parameters=false)
 */
const ParameterStatusButton = ({
  hasActiveParameters,
  parametersFilled,
  onClick,
  disabled = false,
  size = "normal", // "small" | "normal" | "large"
}) => {
  // Determinar estado e cores
  let bgColor, textColor, icon, tooltip, ariaLabel;

  if (!hasActiveParameters) {
    // Produto sem parâmetros configurados
    bgColor = "bg-neutral-200 hover:bg-neutral-300";
    textColor = "text-neutral-500";
    icon = faMinus;
    tooltip = "Sem parâmetros configurados";
    ariaLabel = "Produto sem parâmetros de produção configurados";
  } else if (parametersFilled) {
    // Parâmetros preenchidos
    bgColor = "bg-green-100 hover:bg-green-200";
    textColor = "text-green-700";
    icon = faClipboardCheck;
    tooltip = "Parâmetros preenchidos - Clique para ver/editar";
    ariaLabel = "Parâmetros de produção preenchidos. Clique para editar.";
  } else {
    // Parâmetros pendentes
    bgColor = "bg-red-100 hover:bg-red-200";
    textColor = "text-red-700";
    icon = faClipboardList;
    tooltip = "Parâmetros pendentes - Clique para preencher";
    ariaLabel = "Parâmetros de produção pendentes. Clique para preencher.";
  }

  // Tamanhos
  const sizeClasses = {
    small: "px-2 py-1 text-xs",
    normal: "px-3 py-1.5 text-sm",
    large: "px-4 py-2 text-base",
  };

  const iconSizes = {
    small: "text-xs",
    normal: "text-sm",
    large: "text-base",
  };

  // Se não tem parâmetros configurados, mostrar apenas indicador
  if (!hasActiveParameters) {
    return (
      <span
        className={`inline-flex items-center ${sizeClasses[size]} ${bgColor} ${textColor} rounded cursor-default`}
        title={tooltip}
        aria-label={ariaLabel}
      >
        <FontAwesomeIcon icon={icon} className={iconSizes[size]} />
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`
        inline-flex items-center justify-center
        ${sizeClasses[size]}
        ${bgColor}
        ${textColor}
        rounded
        transition-colors
        focus:outline-none focus:ring-2 focus:ring-offset-1
        ${parametersFilled ? "focus:ring-green-400" : "focus:ring-red-400"}
        disabled:opacity-50 disabled:cursor-not-allowed
      `}
      title={tooltip}
      aria-label={ariaLabel}
    >
      <FontAwesomeIcon icon={icon} className={iconSizes[size]} />
      {size !== "small" && (
        <span className="ml-1.5 hidden sm:inline">
          {parametersFilled ? "OK" : "Preencher"}
        </span>
      )}
    </button>
  );
};

/**
 * Versão compacta do botão para uso em tabelas
 */
export const ParameterStatusIndicator = ({
  hasActiveParameters,
  parametersFilled,
  onClick,
}) => {
  if (!hasActiveParameters) {
    return (
      <span
        className="inline-block w-3 h-3 rounded-full bg-neutral-300"
        title="Sem parâmetros configurados"
      />
    );
  }

  if (parametersFilled) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="inline-block w-3 h-3 rounded-full bg-green-500 hover:bg-green-600 cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-1"
        title="Parâmetros preenchidos - Clique para ver"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-block w-3 h-3 rounded-full bg-red-500 hover:bg-red-600 cursor-pointer animate-pulse focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-1"
      title="Parâmetros pendentes - Clique para preencher"
    />
  );
};

export default ParameterStatusButton;
